import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Helmet } from 'react-helmet-async';
import { toast } from "sonner";
import { Phone, ArrowRight, CheckCircle, Send, ArrowLeft } from "lucide-react";
import apiClient from "@/lib/apiClient";
import Header from "../../header";

interface ThemeConfig {
  nav_color?: string;
  primary_color?: string;
  secondary_color?: string;
  bg_color?: string;
  title_font?: string;
  title_size?: "small" | "medium" | "large" | "xlarge";
  body_font?: string;
  logo_type?: "text" | "image";
  logo_text?: string | null;
  logo_image_url?: string | null;
  design_style?: "neobrutalism" | "modern";
}

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  faq_url: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
  require_phone_verification?: boolean;
  require_birth_date_verification?: boolean;
  point_purchase_enabled?: boolean;
  theme_config?: ThemeConfig;
  site_type?: string;
  show_faq?: boolean;
}

interface VerifyPhonePageProps {
  siteData?: Site;
}

const VerifyPhonePage: React.FC<VerifyPhonePageProps> = ({ siteData }) => {
  const [step, setStep] = useState<1 | 2>(1); // 1: 電話番号入力, 2: 認証コード入力
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);

  const handleSendCode = async () => {
    // バリデーション
    if (!phoneNumber) {
      toast.error("電話番号を入力してください");
      return;
    }

    // 数字のみチェック
    const numbersOnly = phoneNumber.replace(/[^0-9]/g, "");
    if (numbersOnly.length !== 10 && numbersOnly.length !== 11) {
      toast.error("電話番号は10桁または11桁で入力してください");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/phone_verifications", {
        phone_number: numbersOnly,
        method: "sms"
      });
      toast.success("認証コードを送信しました");
      setPhoneNumber(numbersOnly);
      setStep(2);
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("認証コードの送信に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      toast.error("認証コードを入力してください");
      return;
    }

    if (verificationCode.length !== 6) {
      toast.error("認証コードは6桁で入力してください");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/phone_verifications/verify", {
        phone_number: phoneNumber,
        code: verificationCode
      });
      toast.success("電話番号を認証しました");
      setVerified(true);

      // 2秒後にトップページへリダイレクト
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("認証に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await apiClient.post("/phone_verifications", {
        phone_number: phoneNumber,
        method: "sms"
      });
      toast.success("認証コードを再送信しました");
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("認証コードの再送信に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    window.location.href = "/";
  };

  return (
    <>
      <Helmet>
        <title>電話番号認証 | {siteData?.name || 'ショップ'}</title>
        <meta property="og:title" content={`電話番号認証 | ${siteData?.name || 'ショップ'}`} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <Header siteData={siteData} />

        <section className="flex items-center justify-center py-12 px-4 pt-24">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme">
                <Phone className="h-8 w-8 text-black" />
              </div>
              <h2 className="text-3xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">
                電話番号認証
              </h2>
              <p className="mt-2 [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                本人確認のため、電話番号の認証をお願いします
              </p>
            </div>

            <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
              {verified ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                    <CheckCircle className="h-8 w-8 text-black" />
                  </div>
                  <h3 className="text-lg [font-weight:var(--theme-font-weight)] text-black mb-2">
                    認証完了
                  </h3>
                  <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">
                    電話番号の認証が完了しました。
                    <br />
                    まもなくトップページへ移動します...
                  </p>
                </div>
              ) : step === 1 ? (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <Phone className="h-4 w-4" />
                      電話番号
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="09012345678"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      maxLength={11}
                      className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                    <p className="mt-2 text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                      ハイフンなしで入力してください
                    </p>
                  </div>

                  <button
                    onClick={handleSendCode}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    {loading ? "送信中..." : "認証コードを送信"}
                  </button>

                  <div className="text-center">
                    <button
                      onClick={handleSkip}
                      className="[font-weight:var(--theme-font-weight-button)] text-gray-500 hover:text-gray-700"
                    >
                      あとで認証する
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center mb-4 p-4 bg-blue-100 border-2 border-blue-400 rounded-theme">
                    <p className="[font-weight:var(--theme-font-weight-button)] text-blue-700">
                      <span className="[font-weight:var(--theme-font-weight)]">{phoneNumber}</span> に
                      <br />
                      認証コードを送信しました
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="code" className="block text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      認証コード
                    </Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder="123456"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ""))}
                      maxLength={6}
                      className="text-center text-2xl tracking-widest border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                        6桁の数字を入力してください
                      </p>
                      <button
                        onClick={handleResendCode}
                        disabled={loading}
                        className="text-xs [font-weight:var(--theme-font-weight-button)] text-theme-primary hover:opacity-80 disabled:opacity-50"
                      >
                        再送信
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleVerifyCode}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {loading ? "認証中..." : "認証する"}
                  </button>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setStep(1)}
                      className="inline-flex items-center gap-1 [font-weight:var(--theme-font-weight-button)] text-gray-500 hover:text-gray-700"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      電話番号を変更
                    </button>
                    <button
                      onClick={handleSkip}
                      className="[font-weight:var(--theme-font-weight-button)] text-gray-500 hover:text-gray-700"
                    >
                      あとで認証する
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="text-center text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
              <p>
                電話番号は本人確認およびセキュリティ向上のために使用されます。
                <br />
                第三者に提供されることはありません。
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default VerifyPhonePage;
